import { ContentfulImageAPI } from './contentfulUtils'
import Sites from '@toasttab/shared/src/data/Sites.json'
import { isLocaleHidden } from '@toasttab/shared/src/config/localeConfig'
import { SiteSchema } from '../interfaces/Site'

interface LocalizedEntry {
  language: string
  slug?: string
  slugOverride?: string
}

export const getSupportedRegionsOrLocales = (): string[] => {
  return Sites.sites
    .map((site: any) => site.url)
    .filter((url: any) => url && url.length > 0)
    .map((url: any) => (url.startsWith('/') ? url.slice(1) : url))
}

export const getSiteIdByLanguageCode = (language: string) => {
  switch (language) {
    case 'en-GB':
      return 2
    case 'en-IE':
      return 3
    case 'en-CA':
      return 4
    case 'en-AU':
      return 5
    case 'es-US':
      return 11
    case 'zh-US':
      return 12
    default:
      return 1
  }
}

export const getLanguageByCountryCode = (country: string) => {
  switch (country.toUpperCase()) {
    case 'UK':
      return 'en-GB'
    case 'IE':
      return 'en-IE'
    case 'CA':
      return 'en-CA'
    case 'AU':
      return 'en-AU'
    case 'ES-US':
      return 'es-US'
    case 'ZH-US':
      return 'zh-US'
    default:
      return 'en-US'
  }
}

export const getCountryNameByCountryCode = (country: string) => {
  switch (country.toUpperCase()) {
    case 'UK':
      return 'United Kingdom'
    case 'IE':
      return 'Ireland'
    case 'CA':
      return 'Canada'
    case 'AU':
      return 'Australia'
    default:
      return 'United States'
  }
}

export const getLocalizedEntries = (
  slug: string,
  regions: any[],
  currentRegion: string
) => {
  regions = regions.filter((region: any) => region.country !== currentRegion)
  return regions.map((region: any) => {
    const language = getLanguageByCountryCode(region.country)
    return {
      slug: slug,
      language: language,
      siteId: getSiteIdByLanguageCode(language)
    }
  })
}

export const getLocaleBySiteId = (siteId: number) => {
  switch (siteId) {
    case 11:
      return 'es-US'
    default:
      return 'en'
  }
}

export const getAllEntries = (slug: string, regions: any[]) => {
  if (!Array.isArray(regions)) return []
  // Get all contentfulRegionCodes for the input (e.g., ['US', 'UK'])
  const regionCodes = regions.map((region) => region.country?.toUpperCase())

  // From Sites.json, get all languages tied to those regions
  const siteEntries = Sites.sites
    .filter((site: SiteSchema) =>
      regionCodes.includes(site.contentfulRegionCode)
    )
    // Filter out hidden locales (e.g., es-US during pre-launch)
    .filter((site: SiteSchema) => !isLocaleHidden(site.language))

  // Map each site entry to a localized entry
  return siteEntries.map((site: SiteSchema) => {
    const regionOverride = regions.find(
      (r: any) => r.country?.toUpperCase() === site.contentfulRegionCode
    )

    const entry: any = {
      slug: slug,
      language: site.language,
      siteId: site.siteId,
      url: site.url
    }

    // Only include slugOverride if it exists
    if (regionOverride?.slugOverride) {
      entry.slugOverride = regionOverride.slugOverride
    }

    return entry
  })
}

export const getSiteUrl = (defaultSiteUrl: string, language: string) => {
  let siteUrl: string | string[] = defaultSiteUrl.split('/')
  let regionString =
    language && language !== 'en-US'
      ? `${language.toLowerCase().split('-')[1]}`
      : ''
  if (regionString === 'gb') {
    regionString = 'uk'
  }
  if (language && language !== 'en-US') {
    siteUrl.splice(3, 0, regionString)
  }
  siteUrl = siteUrl.join('/')
  return siteUrl
}

export type JSONValue =
  | string
  | number
  | boolean
  | { [x: string]: JSONValue }
  | JSONValue[]

export const isAbsoluteUrl = (url: string): boolean => {
  // Regular expression to check if the URL is absolute (starts with http://, https://, or //)
  return /^(?:[a-z]+:)?\/\//i.test(url)
}

export const prependHostName = (
  data: JSONValue,
  hostName: string
): JSONValue => {
  if (Array.isArray(data)) {
    return data.map((item) => prependHostName(item, hostName))
  } else if (typeof data === 'object' && data !== null) {
    const updatedObject: { [key: string]: JSONValue } = {}
    for (const key in data) {
      if (
        ['demoUrl', 'href', 'linkUrl', 'url'].includes(key) &&
        typeof data[key] === 'string'
      ) {
        const urlValue = data[key] as string
        // Only prepend hostName if the URL is not absolute
        updatedObject[key] = isAbsoluteUrl(urlValue)
          ? urlValue
          : `${hostName}${urlValue}`
      } else {
        updatedObject[key] = prependHostName(data[key], hostName)
      }
    }
    return updatedObject
  }
  return data
}

export const getEntryImages = (hero: any, thumbnailImage?: any) => {
  return [
    {
      heroImage: hero.image && [getImage(hero.image)],
      thumbnailImage: thumbnailImage && [getImage(thumbnailImage)]
    }
  ]
}

export const getImage = (contentfulImage: any) => {
  const image = contentfulImage
  if (image && image.file) {
    const query = ContentfulImageAPI.transformImageAvif()
    const srcset = ContentfulImageAPI.transformImageAvifSrcset({
      url: image.file.url,
      widths: [320, 640, 768, 1024, 1280, 1536, 1920]
    })

    let imageUrl = image.file.url + query
    if (!imageUrl.includes('http')) {
      imageUrl = `https:${imageUrl}`
    }
    return {
      typeHandle: 'image',
      extension: image.file.contentType,
      url: image.file.url,
      title: image.title,
      imgClasses: '',
      optimizedImages: {
        src: image.file.url,
        srcset: srcset,
        srcsetMaxWidth: '',
        srcsetAvif: srcset,
        placeholderBox: '',
        placeholderImage: '',
        maxSrcsetWidth: '',
        srcAvif: imageUrl
      }
    }
  }
}

// Rename oldProperty to newProperty
export const renameProperty = (obj: any, oldProp: string, newProp: string) => {
  if (obj.hasOwnProperty(oldProp)) {
    obj[newProp] = obj[oldProp] // Copy the value to the new property
    delete obj[oldProp] // Delete the old property
  }
}

export const formatUrl = (site: SiteSchema, pkgPath: string, slug: string) => {
  const trimmedPkgPath = pkgPath.replace(/\/+$/, '') // remove trailing slashes
  const normalizedSlug = slug.replace(/^\/+/, '') // remove leading slashes

  // Avoid duplicate slug if it's already at the end of the path
  const shouldSkipSlug =
    trimmedPkgPath.endsWith(`/${normalizedSlug}`) ||
    trimmedPkgPath === normalizedSlug

  const url = shouldSkipSlug
    ? `${site.url}${trimmedPkgPath}`
    : `${site.url}${trimmedPkgPath}/${normalizedSlug}`

  return url.replace(/\/+$/, '') // remove any trailing slash
}

export const normalizeRegion = (regionCode?: string) =>
  regionCode?.toUpperCase() === 'UK' ? 'GB' : regionCode?.toUpperCase() || ''

export const normalizeLang = (lang = '') =>
  lang && lang.toLowerCase().split('-')[0]

export const getRegionLabel = (
  languageName: string,
  regionCode?: string,
  activeRegionCode?: string
) => {
  const isBothUS =
    normalizeRegion(regionCode) === 'US' &&
    normalizeRegion(activeRegionCode) === 'US'

  if (isBothUS) {
    // Strip region and keep language only "English"
    const match = languageName.match(/\((.*?)\)/)
    return match?.[1] || languageName
  }

  return languageName
}

export const getRegionCode = (region: string | undefined) => {
  if (!region) return 'US'
  if (region.toUpperCase() === 'UK') return 'GB'
  return Sites.sites
    .filter((site) => site.defaultRegion)
    .some((site) => site.craftSite === region.toUpperCase())
    ? region.toUpperCase()
    : 'US'
}

export const groupSitesByContinent = (
  sites: SiteSchema[],
  suggestedLanguage: string,
  localizedEntries: LocalizedEntry[],
  activeLanguage: string,
  path: string
): Record<string, (SiteSchema & { url: string; slug: string })[]> =>
  sites.reduce((acc, site) => {
    if (site.language === suggestedLanguage) return acc

    const entry = localizedEntries.find((e) => e.language === site.language)

    // If page doesn't exist in target language, redirect to homepage
    let slug = ''
    let url = site.url || '/'

    if (entry) {
      // Page exists in target language, use the specific slug
      slug = entry.slugOverride || entry.slug || ''
      url = formatUrl(site, path, slug)
    }
    // If no entry exists, keep url as homepage (site.url)

    const siteWithUrl = { ...site, url, slug }

    acc[site.continent] = acc[site.continent] || []
    acc[site.continent].push(siteWithUrl)

    return acc
  }, {} as Record<string, (SiteSchema & { url: string; slug: string })[]>)

export function generateUUID(): string {
  // The 'x' and 'y' placeholders are replaced with random hexadecimal digits.
  // The 'y' part also includes logic to set the correct UUID version (4) and variant bits.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
    /[xy]/g,
    function (c: string): string {
      // Generate a random number between 0 and 15 (inclusive)
      const r: number = (Math.random() * 16) | 0

      // If 'x', use r; if 'y', set variant bits according to UUID spec
      const v: number = c === 'x' ? r : (r & 0x3) | 0x8

      // Convert the number to its hexadecimal representation
      return v.toString(16)
    }
  )
}

/**
 * Extracts language code from URL pathname using Sites.json data
 * Maps URL paths to proper language codes based on site configuration
 * @param pathname - The URL pathname (e.g., '/es-us/some-page', '/uk/page')
 * @returns The proper language code (e.g., 'es-us', 'en-gb', 'en-us')
 */
export const getLanguageFromPath = (pathname: string): string => {
  // Extract the first path segment (e.g., '/es-us/some-page' -> 'es-us')
  const pathSegments = pathname.split('/').filter(Boolean)
  const firstSegment = pathSegments[0] || ''

  // Find matching site by URL path (case insensitive)
  const matchingSite = Sites.sites.find((site) => {
    // Only match exact URL paths, not the default empty site
    if (site.url === `/${firstSegment}`) return true
    // Handle case insensitive matching
    if (site.url.toLowerCase() === `/${firstSegment.toLowerCase()}`) return true
    return false
  })

  // Return the language from the matching site, or default to 'en'
  const language = matchingSite?.language || 'en'

  // Convert 'en-US' to 'en' for consistency (only for US default)
  return language === 'en-US' ? 'en' : language
}
